import React, {useCallback, useContext} from 'react';


import {FormInput} from 'src/components/input';
import {MessageContext} from 'src/contexts/message-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';

import {FlatButton} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {CustomGroupItem} from '@pb/kit-component-input-v2';
import {Tooltip} from '@pb/kit-component-popups';

import type {ConfirmTypes} from '../constants';
import type {OnGenerateKey} from '../typedef';

type Props = {
    label: string,
    disabled: boolean,
    keyValue: string,
    confirmType: ConfirmTypes,
    onGenerateKey: OnGenerateKey,
};

export const KeyControl = (props: Props) => {
    const {confirmType, keyValue, onGenerateKey: onGenerateKeyProp, label, disabled} = props;

    const {setSuccessMessage} = useContext(MessageContext);
    const {hasAccess} = useAdminAccessRight();

    const onCopyKey = useCallback(() => {
        navigator.clipboard.writeText(keyValue);
        setSuccessMessage('Key is copied to your clipboard');
    }, [keyValue, setSuccessMessage]);

    const onGenerateKey = useCallback(() => {
        onGenerateKeyProp(confirmType);
    }, [confirmType, onGenerateKeyProp]);

    const isValue = !!keyValue;
    const canGenerate = !disabled && isValue && hasAccess(ACCESS_RIGHTS.API_V2_SCREEN_EDIT);
    const isBusy = !disabled && !isValue;

    return (
        <Group direction="v" gap="s">
            <div className="api-v2-setup__key-control">
                {isValue && !disabled ? (
                    <Tooltip
                        color="light"
                        placement="right"
                        anchor={({anchorRef, getAnchorProps}) => (
                            <div
                                ref={anchorRef}
                                className="api-v2-setup__key-control-shield api-v2-setup__key-control-shield_active"
                                onClick={onCopyKey}
                                {...getAnchorProps()}>
                                <CustomGroupItem {...{label}}>
                                    <FormInput{...{
                                        id: 'api-v2-key',
                                        value: keyValue,
                                        readOnly: true,
                                        onBlur: () => undefined,
                                    }}
                                    />
                                </CustomGroupItem>
                            </div>
                        )}>
                        Click to copy
                    </Tooltip>
                ) : (
                    <div className="api-v2-setup__key-control-shield">
                        <CustomGroupItem {...{label}}>
                            <FormInput{...{
                                id: 'api-v2-key',
                                value: keyValue,
                                readOnly: true,
                                disabled: true,
                                onBlur: () => undefined,
                            }}
                            />
                        </CustomGroupItem>
                    </div>
                )}
            </div>
            <FlatButton
                style={{width: 140}}
                busy={isBusy}
                disabled={!canGenerate}
                startIcon={<Icon type={Icons.REPEAT}/>}
                onClick={onGenerateKey}>
                <b>Generate new key</b>
            </FlatButton>
        </Group>
    );
};

